import { useCallback, useContext, useMemo } from 'react';
import { buildCapabilitiesFromConfig, buildFormattingTrayFromConfig } from './utils/buildCapabilitiesFromConfig';
import styled from 'styled-components';
import { EditorStateContext } from './contexts/EditorStateContext';
import { FocusPlugin } from './plugins/FocusPlugin';
import { SubmitButtonContext } from './contexts/SubmitButtonContext';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { jsx as _jsx } from "react/jsx-runtime";
function ToolbarFocusProvider({
  children
}) {
  const {
    editorState,
    setEditorState
  } = useContext(EditorStateContext);
  const focusingSetEditorState = useCallback(newState => {
    if (!setEditorState || !editorState) return;
    // FocusPlugin.apply returns the previous plugin state by reference when
    // a transaction sets no meta, so a stale focusView from an earlier
    // transaction can persist. Compare references to detect whether the
    // current transaction explicitly set new FocusPlugin meta.
    const prevFocusMeta = FocusPlugin.getState(editorState);
    const nextFocusMeta = FocusPlugin.getState(newState);
    if (nextFocusMeta !== null && nextFocusMeta !== void 0 && nextFocusMeta.focusView && nextFocusMeta !== prevFocusMeta) {
      setEditorState(newState);
      return;
    }
    const focusTr = newState.tr.setMeta(FocusPlugin, {
      focusView: true,
      preserveSelection: true
    });
    if (newState.storedMarks) {
      focusTr.setStoredMarks(newState.storedMarks);
    }
    setEditorState(newState.apply(focusTr));
  }, [editorState, setEditorState]);
  const value = useMemo(() => ({
    editorState,
    setEditorState: focusingSetEditorState
  }), [editorState, focusingSetEditorState]);
  return /*#__PURE__*/_jsx(EditorStateContext.Provider, {
    value: value,
    children: children
  });
}
const StyledWrapper = styled.div.withConfig({
  displayName: "ProsemirrorMenu__StyledWrapper"
})(["padding-top:", ";"], ({
  theme
}) => theme.space['200']);
export default function ProsemirrorMenu(props) {
  const {
    editorConfig,
    menuClassName = '',
    submitButton
  } = props;
  const memoizedSubmitButton = useMemo(() => {
    return {
      submitButton
    };
  }, [submitButton]);
  const toolbarCapabilities = useMemo(() => {
    return buildCapabilitiesFromConfig(editorConfig);
  }, [editorConfig]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(StyledWrapper, {
      className: `prosemirror-controls ${menuClassName}`,
      children: /*#__PURE__*/_jsx(ToolbarFocusProvider, {
        children: /*#__PURE__*/_jsx(SubmitButtonContext.Provider, {
          value: memoizedSubmitButton,
          children: /*#__PURE__*/_jsx(TFlex, {
            alignItems: "center",
            flexWrap: "wrap",
            children: toolbarCapabilities
          })
        })
      })
    })
  );
}
export function ProsemirrorFormattingTray(props) {
  const {
    editorConfig,
    menuClassName = ''
  } = props;
  const formattingToolbarCapabilities = useMemo(() => {
    return buildFormattingTrayFromConfig(editorConfig);
  }, [editorConfig]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(StyledWrapper, {
      className: `prosemirror-formatting-tray ${menuClassName}`,
      children: /*#__PURE__*/_jsx(ToolbarFocusProvider, {
        children: /*#__PURE__*/_jsx(TFlex, {
          alignItems: "center",
          flexWrap: "wrap",
          children: formattingToolbarCapabilities
        })
      })
    })
  );
}